import { UseFormReturn } from 'react-hook-form';
import { MiscellaneousField } from '../../reportsTypes';
import { useState } from 'react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  SelectType,
  Button,
  Badge,
  ScrollArea,
} from '@ais-ui/ui-core';
import { GripVertical, ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';

interface MultiSelectDualDragNDropPanelItemProps {
  fieldName: string;
  form: UseFormReturn;
  fieldInfo: MiscellaneousField;
}

export const MiscellaneousMultiSelectDualDragNDropPanelItem = ({
  fieldName,
  form,
  fieldInfo,
}: MultiSelectDualDragNDropPanelItemProps) => {
  const [selectables] = useState<SelectType[]>(
    fieldInfo.options as SelectType[],
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredAvailable, setHoveredAvailable] = useState<string | null>(null);
  const { messages } = useLocalization();

  // No dynamic fetching - only use static options from fieldInfo.options

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        // Convert between SelectType[] (form data) and string[] (internal state)
        type SelectedField = SelectType | string;
        const selectedFields = (field.value as SelectedField[]) || [];
        const selectedValues = selectedFields.map((item) =>
          typeof item === 'string' ? item : item.value,
        );
        const sanitizedFieldId = fieldName.replace(/[^a-zA-Z0-9_-]/g, '-');

        const unselectedFields = selectables.filter(
          (selectable) => !selectedValues.includes(selectable.value),
        );

        const handleAddField = (value: string) => {
          const selectableItem = selectables.find((s) => s.value === value);
          if (selectableItem) {
            const newSelected = [...selectedFields, selectableItem];
            field.onChange(newSelected);
            form.clearErrors();
          }
        };

        const handleRemoveField = (value: string) => {
          const newSelected = selectedFields.filter((item) => {
            const itemValue = typeof item === 'string' ? item : item.value;
            return itemValue !== value;
          });
          field.onChange(newSelected);
        };

        const getFieldLabel = (value: string) => {
          const selectable = selectables.find((s) => s.value === value);
          return selectable ? selectable.label : value;
        };

        const handleDragStart = (index: number) => {
          setDraggedIndex(index);
        };

        const handleDragOver = (e: React.DragEvent, index: number) => {
          e.preventDefault();
          if (draggedIndex === null || draggedIndex === index) return;

          const newFields = [...selectedFields];
          const draggedField = newFields[draggedIndex];
          newFields.splice(draggedIndex, 1);
          newFields.splice(index, 0, draggedField);

          field.onChange(newFields);
          setDraggedIndex(index);
        };

        const handleDragEnd = () => {
          setDraggedIndex(null);
        };

        return (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {fieldInfo.name}: {fieldInfo.required && '*'}
              </FormLabel>
              {fieldInfo.help && (
                <InfoIconTooltip className="mr-2">
                  {fieldInfo.help}
                </InfoIconTooltip>
              )}
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Available Fields Panel */}
                <div className="border rounded-lg p-3 h-fit">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">
                        Available Fields
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {unselectedFields.length} field
                        {unselectedFields.length !== 1 ? 's' : ''} available
                      </p>
                    </div>
                    {unselectedFields.length > 0 && (
                      <Button
                        id={`${sanitizedFieldId}-add-all-button`}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allSelectableItems = unselectedFields.map(
                            (f) => f,
                          );
                          field.onChange([
                            ...selectedFields,
                            ...allSelectableItems,
                          ]);
                        }}
                      >
                        Add all
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-1 pr-4">
                      {unselectedFields.length === 0 ? (
                        <div className="flex items-center justify-center h-[148px] text-sm text-muted-foreground">
                          All fields selected
                        </div>
                      ) : (
                        unselectedFields.map((selectable) => (
                          <div
                            key={selectable.value}
                            className={cn(
                              'flex items-center justify-between p-1.5 rounded-md transition-colors cursor-pointer',
                              hoveredAvailable === selectable.value
                                ? 'bg-muted'
                                : 'hover:bg-muted/50',
                            )}
                            onMouseEnter={() =>
                              setHoveredAvailable(selectable.value)
                            }
                            onMouseLeave={() => setHoveredAvailable(null)}
                            onClick={() => handleAddField(selectable.value)}
                          >
                            <span className="text-sm flex-1 truncate">
                              {selectable.label}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddField(selectable.value);
                              }}
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Selected Fields Panel */}
                <div className="border rounded-lg p-3 h-fit">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">
                        Selected Fields (export order)
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Drag to reorder • {selectedFields.length} selected
                      </p>
                    </div>
                    {selectedFields.length > 0 && (
                      <Button
                        id={`${sanitizedFieldId}-clear-button`}
                        variant="ghost"
                        size="sm"
                        onClick={() => field.onChange([])}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-1.5 pr-4 mr-2">
                      {selectedFields.length === 0 ? (
                        <div className="flex items-center justify-center h-[148px] text-sm text-muted-foreground">
                          {messages.miscNoFieldsSelected}
                        </div>
                      ) : (
                        selectedFields.map((item, index) => {
                          const itemValue =
                            typeof item === 'string' ? item : item.value;
                          return (
                            <div
                              key={itemValue}
                              draggable
                              onDragStart={() => handleDragStart(index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragEnd={handleDragEnd}
                              className={cn(
                                'flex items-center gap-2 p-1.5 bg-muted/30 border rounded-md cursor-move transition-all hover:bg-muted mr-2',
                                draggedIndex === index && 'opacity-50',
                              )}
                            >
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              <Badge
                                variant="secondary"
                                className="font-mono text-xs flex-shrink-0 h-5 px-1.5"
                              >
                                {index + 1}
                              </Badge>
                              <span
                                className="flex-1 text-sm truncate min-w-0 max-w-[180px]"
                                title={getFieldLabel(itemValue)}
                              >
                                {getFieldLabel(itemValue)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 flex-shrink-0"
                                onClick={() => handleRemoveField(itemValue)}
                              >
                                <ChevronLeft className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
